/* Lodge fitting room: a line-up of figures dressed in real regalia, ready to order as one lodge */
(() => {
  const root = document.querySelector('[data-fr]');
  if (!root) return;
  const DATA = JSON.parse(root.querySelector('[data-fr-data]').textContent);
  const calm = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (s) => root.querySelector(s);
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const KEY = 'riaz-fitting-v1';
  const KINDS = ['apron', 'collar', 'jewel'];
  const LIST = { apron: 'aprons', collar: 'collars', jewel: 'jewels' };

  const offices = DATA.offices.filter((o) => o.aprons.length);
  const byId = Object.fromEntries(offices.map((o) => [o.id, o]));
  const MONEY = (DATA.money || '${{amount}}').replace(/<[^>]*>/g, '');
  const money = (cents) => {
    const v = cents / 100;
    const group = (n, dp) => n.toFixed(dp).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return MONEY.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) =>
      k === 'amount_no_decimals' ? group(Math.round(v), 0)
        : k === 'amount_with_comma_separator' ? group(v, 2).replace(/,/g, ' ').replace('.', ',')
          : group(v, 2));
  };

  /* ---------- What each figure wears ---------- */
  const includesJewel = (p) => p && /jewel|pendant/i.test(p.title);
  const find = (o, kind, handle) => handle && (o[LIST[kind]] || []).find((p) => p.handle === handle);
  const variantOf = (p, id) => p.variants.find((v) => v.id === id) || p.variants.find((v) => v.available) || p.variants[0];
  const pick = (list, style) => {
    if (!list.length) return null;
    if (style === 'embroidered') {
      const fine = list.find((p) => /hand embroider|bullion/i.test(p.title));
      if (fine) return fine;
    }
    return list.find((p) => /royal blue/i.test(p.title)) || list[0];
  };
  const choice = (p) => p && { p: p.handle, v: variantOf(p).id };
  const dress = (o, style) => {
    const collar = o.kind === 'members' ? null : o.collars.find((p) => !includesJewel(p)) || null;
    return {
      id: o.id,
      apron: choice(pick(o.aprons, style)),
      collar: choice(collar),
      jewel: o.kind === 'members' ? null : choice(o.jewels[0]),
      qty: o.kind === 'members' ? Math.max(1, o.quantity || 10) : 1
    };
  };

  let state = { cast: [], sel: null, gloves: false, style: 'royal', view: 'close', tab: 'apron' };
  const entry = (id) => state.cast.find((e) => e.id === id);
  const current = () => entry(state.sel) || state.cast[0];

  const presets = {
    principal: () => offices.filter((o) => o.principal),
    officers: () => offices.filter((o) => o.kind === 'office'),
    lodge: () => offices
  };

  /* ---------- Saving, sharing and restoring ---------- */
  const encode = () => state.cast.map((e) => {
    const o = byId[e.id];
    const part = (kind) => {
      const s = e[kind], list = o[LIST[kind]] || [];
      if (!s) return '-';
      const p = list.findIndex((x) => x.handle === s.p);
      const v = p < 0 ? 0 : list[p].variants.findIndex((x) => x.id === s.v);
      return `${p}.${Math.max(0, v)}`;
    };
    return [e.id, part('apron'), part('collar'), part('jewel'), e.qty].join('_');
  }).join('~') + `|${state.gloves ? 1 : 0}|${state.style}`;

  const decode = (text) => {
    const [cast, gloves, style] = text.split('|');
    const out = [];
    (cast || '').split('~').forEach((chunk) => {
      const [id, a, c, j, q] = chunk.split('_'), o = byId[id];
      if (!o) return;
      const part = (kind, code) => {
        if (!code || code === '-') return null;
        const [pi, vi] = code.split('.').map(Number), p = (o[LIST[kind]] || [])[pi];
        return p ? { p: p.handle, v: (p.variants[vi] || p.variants[0]).id } : null;
      };
      out.push({ id, apron: part('apron', a) || dress(o, 'royal').apron, collar: part('collar', c), jewel: part('jewel', j), qty: Math.max(1, Math.min(500, +q || 1)) });
    });
    return out.length ? { cast: out, gloves: gloves === '1', style: style === 'embroidered' ? 'embroidered' : 'royal' } : null;
  };

  const save = () => { try { localStorage.setItem(KEY, encode()); } catch {} };
  const restore = () => {
    const hash = decodeURIComponent(location.hash || '');
    if (hash.startsWith('#lodge=')) {
      const s = decode(hash.slice(7));
      if (s) return s;
    }
    try { const t = localStorage.getItem(KEY); if (t) return decode(t); } catch {}
    return null;
  };

  /* ---------- Taking the white background off product photographs ---------- */
  const cache = new Map();
  const cutout = (src, kind) => {
    const key = `${kind}|${src}`;
    if (!cache.has(key)) {
      cache.set(key, new Promise((resolve) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => process(img, kind).then(resolve, () => resolve(null));
        img.onerror = () => resolve(null);
        img.src = src.startsWith('//') ? location.protocol + src : src;
      }));
    }
    return cache.get(key);
  };

  const process = async (img, kind) => {
    const W = Math.min(640, img.naturalWidth), H = Math.round(img.naturalHeight * W / img.naturalWidth);
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, W, H);
    const data = ctx.getImageData(0, 0, W, H), d = data.data, N = W * H;
    const light = (p) => {
      if (d[p * 4 + 3] < 16) return true;
      const r = d[p * 4], g = d[p * 4 + 1], b = d[p * 4 + 2], lo = Math.min(r, g, b);
      return lo >= 226 && Math.max(r, g, b) - lo <= 24;
    };
    // flood fill from the edges, so white inside the regalia stays white
    const bg = new Uint8Array(N), stack = [];
    for (let x = 0; x < W; x++) stack.push(x, (H - 1) * W + x);
    for (let y = 0; y < H; y++) stack.push(y * W, y * W + W - 1);
    while (stack.length) {
      const p = stack.pop();
      if (bg[p] || !light(p)) continue;
      bg[p] = 1;
      const x = p % W;
      if (x > 0) stack.push(p - 1);
      if (x < W - 1) stack.push(p + 1);
      if (p >= W) stack.push(p - W);
      if (p < N - W) stack.push(p + W);
    }
    let cleared = 0;
    for (let p = 0; p < N; p++) {
      if (bg[p]) { d[p * 4 + 3] = 0; cleared++; continue; }
      const x = p % W;
      const edge = (x > 0 && bg[p - 1]) || (x < W - 1 && bg[p + 1]) || (p >= W && bg[p - W]) || (p < N - W && bg[p + W]);
      if (edge) {
        const lo = Math.min(d[p * 4], d[p * 4 + 1], d[p * 4 + 2]);
        d[p * 4 + 3] = Math.max(60, Math.min(255, (255 - lo) * 4));
      }
    }
    // the visible piece, without the empty margins
    let x0 = W, y0 = H, x1 = -1, y1 = -1;
    const rowSpan = new Int32Array(H);
    for (let y = 0; y < H; y++) {
      let a = -1, b = -1;
      for (let x = 0; x < W; x++) {
        if (d[(y * W + x) * 4 + 3] > 40) { if (a < 0) a = x; b = x; }
      }
      if (a >= 0) {
        rowSpan[y] = b - a;
        if (a < x0) x0 = a;
        if (b > x1) x1 = b;
        if (y < y0) y0 = y;
        y1 = y;
      }
    }
    if (x1 < 0) return null;
    if (kind === 'apron') {
      // leave the waist strap behind: start where the apron reaches its full width
      const widest = Math.max(...rowSpan);
      for (let y = y0; y < y1; y++) {
        if (rowSpan[y] >= widest * 0.74) { y0 = Math.max(y0, y - Math.round(H * 0.006)); break; }
      }
    }
    ctx.putImageData(data, 0, 0);
    const out = document.createElement('canvas');
    out.width = x1 - x0 + 1; out.height = y1 - y0 + 1;
    out.getContext('2d').drawImage(canvas, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
    const blob = await new Promise((r) => out.toBlob(r, 'image/png'));
    return { url: blob ? URL.createObjectURL(blob) : out.toDataURL(), w: out.width, h: out.height };
  };

  /* ---------- The figure ---------- */
  const SVGNS = 'http://www.w3.org/2000/svg';
  const mirror = (d) => d.replace(/(-?\d+(?:\.\d+)?) (-?\d+(?:\.\d+)?)/g, (_, x, y) => `${400 - +x} ${y}`);
  const SLEEVE = 'M104 214 C88 222 80 244 78 270 L70 470 C69 490 70 506 72 516 L104 518 C106 500 108 480 110 462 L118 300 Z';
  const HAND = 'M73 512 C69 532 71 558 81 572 C91 583 104 577 106 561 C108 545 106 527 104 514 Z';
  const LEG = 'M110 536 L198 536 L194 600 L190 924 L146 924 L132 700 Z';
  const SHOE = 'M142 919 L192 919 C195 929 195 940 190 946 L128 946 C126 935 132 923 142 919 Z';
  const LAPEL = 'M176 192 L150 204 L140 236 L156 286 L188 342 L197 322 L184 290 L170 236 Z';
  const COLLARPT = 'M178 192 L197 205 L185 222 L173 200 Z';
  const BASE = `
    <ellipse cx="200" cy="950" rx="118" ry="15" fill="url(#frShadow)"/>
    <path d="${LEG}" fill="url(#frLeg)"/><path d="${mirror(LEG)}" fill="url(#frLeg)"/>
    <path d="M168 600 L164 900 M232 600 L236 900" stroke="#ffffff10" stroke-width="2"/>
    <path d="${SHOE}" fill="url(#frShoe)"/><path d="${mirror(SHOE)}" fill="url(#frShoe)"/>
    <path d="M176 192 C150 196 126 202 108 210 C100 214 96 224 96 238 L102 340 C106 380 112 420 116 458 C114 486 110 512 108 540 L292 540 C290 512 286 486 284 458 C288 420 294 380 298 340 L304 238 C304 224 300 214 292 210 C274 202 250 196 224 192 Z" fill="url(#frSuit)"/>
    <path d="M200 312 L200 540" stroke="#05070f" stroke-width="2"/>
    <path d="M128 470 L170 466 M230 466 L272 470" stroke="#05070f99" stroke-width="2.4"/>
    <circle cx="205" cy="372" r="3.6" fill="#2b3255"/><circle cx="205" cy="424" r="3.6" fill="#2b3255"/>
    <path d="M178 192 L222 192 L210 296 L200 312 L190 296 Z" fill="#f4f1ea"/>
    <path d="M193 204 L207 204 L205 216 L195 216 Z" fill="#0d1020"/><path d="M195 216 L205 216 L210 292 L200 306 L190 292 Z" fill="url(#frTie)"/>
    <path d="${COLLARPT}" fill="#ffffff"/><path d="${mirror(COLLARPT)}" fill="#ffffff"/>
    <path d="${LAPEL}" fill="url(#frLapel)" stroke="#ffffff14" stroke-width="1.2"/><path d="${mirror(LAPEL)}" fill="url(#frLapel)" stroke="#ffffff14" stroke-width="1.2"/>
    <path d="M136 262 L156 258 L150 270 Z" fill="#f4f1ea"/>
    <path d="${SLEEVE}" fill="url(#frSleeve)"/><path d="${mirror(SLEEVE)}" fill="url(#frSleeve)"/>
    <path d="M72 506 L104 508 M328 506 L296 508" stroke="#f4f1ea" stroke-width="5"/>
    <g class="fr-hands"><path d="${HAND}"/><path d="${mirror(HAND)}"/></g>`;
  const TOP = `
    <path d="M181 138 C182 158 180 176 176 194 C188 200 212 200 224 194 C220 176 218 158 219 138 Z" fill="url(#frSkin)"/>
    <path d="M200 36 C229 36 247 60 247 97 C247 128 231 152 200 155 C169 152 153 128 153 97 C153 60 171 36 200 36 Z" fill="url(#frHead)"/>`;
  const CHAIN = '<path d="M178 196 C182 236 192 272 200 298 C208 272 218 236 222 196" fill="none" stroke="#05070e" stroke-width="5" stroke-linecap="round" opacity=".5"/><path d="M178 196 C182 236 192 272 200 298 C208 272 218 236 222 196" fill="none" stroke="#e4e6ea" stroke-width="2.6" stroke-dasharray="4 2.5" stroke-linecap="round"/>';
  const DEFS = `
    <linearGradient id="frSuit" x1="0" x2="1"><stop offset="0" stop-color="#0b0f20"/><stop offset=".32" stop-color="#1b2142"/><stop offset=".55" stop-color="#1e2547"/><stop offset="1" stop-color="#0a0e1d"/></linearGradient>
    <linearGradient id="frSleeve" x1="0" x2="1"><stop offset="0" stop-color="#090c1a"/><stop offset=".5" stop-color="#1a203f"/><stop offset="1" stop-color="#0d1124"/></linearGradient>
    <linearGradient id="frLeg" x1="0" x2="1"><stop offset="0" stop-color="#0a0d1c"/><stop offset=".5" stop-color="#191f3c"/><stop offset="1" stop-color="#0a0d1b"/></linearGradient>
    <linearGradient id="frLapel" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0a0d1b"/><stop offset="1" stop-color="#161c37"/></linearGradient>
    <linearGradient id="frTie" x1="0" x2="1"><stop offset="0" stop-color="#05070e"/><stop offset=".5" stop-color="#1c2140"/><stop offset="1" stop-color="#05070e"/></linearGradient>
    <linearGradient id="frShoe" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#1e2029"/><stop offset="1" stop-color="#040506"/></linearGradient>
    <radialGradient id="frSkin" cx=".4" cy=".3" r=".9"><stop offset="0" stop-color="#f8f1e5"/><stop offset=".65" stop-color="#e3d6c1"/><stop offset="1" stop-color="#c5b597"/></radialGradient>
    <radialGradient id="frHead" cx=".36" cy=".3" r=".85"><stop offset="0" stop-color="#fbf6ec"/><stop offset=".6" stop-color="#e6dac6"/><stop offset="1" stop-color="#c3b292"/></radialGradient>
    <radialGradient id="frShadow"><stop offset="0" stop-color="#0b1026" stop-opacity=".42"/><stop offset="1" stop-color="#0b1026" stop-opacity="0"/></radialGradient>`;

  const defs = document.createElementNS(SVGNS, 'svg');
  defs.setAttribute('class', 'fr-defs');
  defs.setAttribute('aria-hidden', 'true');
  defs.innerHTML = `<defs>${DEFS}</defs>`;
  root.prepend(defs);

  const bodyMarkup = () => `
    <svg class="fr-man" viewBox="0 0 400 1000" aria-hidden="true">${BASE}</svg>
    <div class="fr-lay" data-lay="apron"></div>
    <div class="fr-lay" data-lay="collar"></div>
    <svg class="fr-man fr-top" viewBox="0 0 400 1000" aria-hidden="true">${TOP}</svg>
    <svg class="fr-man fr-chain" viewBox="0 0 400 1000" aria-hidden="true">${CHAIN}</svg>
    <div class="fr-lay" data-lay="jewel"></div>`;

  // where each piece sits on the figure, in the figure's 400 by 1000 space
  const fit = (kind, w, h, collarTip) => {
    const box = { apron: [182, 214, 452], collar: [178, 290, 170], jewel: [50, 76, 0] }[kind];
    let width = box[0], height = width * h / w;
    if (height > box[1]) { height = box[1]; width = height * w / h; }
    const top = kind === 'jewel' ? (collarTip ?? 298) - 8 : box[2];
    return { left: 200 - width / 2, top, width, height };
  };

  const figs = new Map();
  const line = $('[data-fr-line]');

  const makeFig = (o) => {
    const el = document.createElement('div');
    el.className = `fr-fig${o.kind === 'members' ? ' fr-fig--group' : ''}`;
    el.dataset.id = o.id;
    el.setAttribute('role', 'option');
    el.tabIndex = -1;
    el.innerHTML = `
      ${o.kind === 'members' ? `<div class="fr-body fr-ghost fr-ghost--l">${bodyMarkup()}</div><div class="fr-body fr-ghost fr-ghost--r">${bodyMarkup()}</div>` : ''}
      <div class="fr-body fr-main-body">${bodyMarkup()}<span class="fr-count" hidden></span></div>
      <p class="fr-plate"><span class="fr-plate-name">${esc(o.short || o.title)}</span><span class="fr-plate-sub"></span></p>`;
    el.setAttribute('aria-label', o.title);
    figs.set(o.id, el);
    return el;
  };

  const setLayer = async (el, kind, product) => {
    const token = (el[`_${kind}`] = (product && product.img) || '');
    const bodies = el.querySelectorAll('.fr-body');
    const result = product && product.img ? await cutout(product.img, kind) : null;
    if (el[`_${kind}`] !== token) return; // a newer choice arrived meanwhile
    if (kind === 'collar') {
      el._collarTip = null;
      if (result && !result.raw) {
        const r = fit('collar', result.w, result.h);
        el._collarTip = r.top + r.height * 0.97;
      }
      el.classList.toggle('has-collar', !!(result && !result.raw));
      const jewel = entry(el.dataset.id)?.jewel;
      placeJewel(el);
      if (!jewel) el.classList.remove('has-jewel');
    }
    bodies.forEach((body) => {
      const lay = body.querySelector(`[data-lay="${kind}"]`);
      lay.querySelectorAll('img').forEach((old) => {
        old.classList.add('is-out');
        setTimeout(() => old.remove(), calm ? 0 : 520);
      });
      if (!result || result.raw) return;
      const r = fit(kind, result.w, result.h, el._collarTip);
      const img = document.createElement('img');
      img.alt = '';
      img.src = result.url;
      img.dataset.kind = kind;
      Object.assign(img.style, { left: `${r.left / 4}%`, top: `${r.top / 10}%`, width: `${r.width / 4}%` });
      if (!calm) img.classList.add('is-in');
      lay.append(img);
      requestAnimationFrame(() => requestAnimationFrame(() => img.classList.remove('is-in')));
    });
    if (kind === 'jewel') el.classList.toggle('has-jewel', !!(result && !result.raw));
  };

  const placeJewel = (el) => {
    const top = (el._collarTip ?? 298) - 8;
    el.querySelectorAll('[data-lay="jewel"] img:not(.is-out)').forEach((img) => { img.style.top = `${top / 10}%`; });
  };

  const dressFig = (el, e, changed) => {
    const o = byId[e.id];
    KINDS.forEach((kind) => {
      if (changed && !changed.includes(kind)) return;
      const s = e[kind];
      setLayer(el, kind, s ? find(o, kind, s.p) : null);
    });
    const count = el.querySelector('.fr-count');
    if (o.kind === 'members') { count.hidden = false; count.textContent = `× ${e.qty}`; }
    el.querySelector('.fr-plate-sub').textContent = o.kind === 'members' ? `${e.qty} brethren` : (o.jewelText || '').replace(/^Jewel of office:\s*/i, '');
  };

  /* ---------- Line-up with smooth reflow ---------- */
  const renderLine = () => {
    const before = new Map();
    figs.forEach((el, id) => { if (el.isConnected) before.set(id, el.getBoundingClientRect()); });
    const wanted = state.cast.map((e) => e.id);
    figs.forEach((el, id) => {
      if (!wanted.includes(id) && el.isConnected && !el.classList.contains('is-leaving')) {
        el.classList.add('is-leaving');
        setTimeout(() => { el.remove(); el.classList.remove('is-leaving'); }, calm ? 0 : 420);
      }
    });
    state.cast.forEach((e, i) => {
      let el = figs.get(e.id);
      const fresh = !el;
      if (fresh) el = makeFig(byId[e.id]);
      if (!el.isConnected || el.classList.contains('is-leaving')) {
        el.classList.remove('is-leaving');
        el.classList.add('is-new');
        setTimeout(() => el.classList.remove('is-new'), 900);
      }
      const at = line.children[i];
      if (at !== el) line.insertBefore(el, at || null);
      if (fresh) dressFig(el, e);
    });
    if (!calm) {
      figs.forEach((el, id) => {
        const was = before.get(id);
        if (!was || !el.isConnected || el.classList.contains('is-leaving')) return;
        const now = el.getBoundingClientRect(), dx = was.left - now.left;
        if (Math.abs(dx) < 1) return;
        el.animate([{ transform: `translateX(${dx}px)` }, { transform: 'none' }], { duration: 620, easing: 'cubic-bezier(.16,1,.3,1)' });
      });
    }
    sizeView();
    markSelected();
  };

  const markSelected = (scroll = true) => {
    const e = current();
    figs.forEach((el, id) => {
      const on = e && id === e.id;
      el.classList.toggle('is-on', on);
      el.setAttribute('aria-selected', String(on));
      el.tabIndex = on ? 0 : -1;
    });
    root.classList.toggle('fr-empty', !state.cast.length);
    if (scroll && e) {
      const el = figs.get(e.id);
      if (el && el.isConnected && line.scrollWidth > line.clientWidth + 4) {
        const left = el.offsetLeft - (line.clientWidth - el.offsetWidth) / 2;
        line.scrollTo({ left: Math.max(0, left), behavior: calm ? 'auto' : 'smooth' });
      }
    }
  };

  const sizeView = () => {
    const n = Math.max(1, line.querySelectorAll('.fr-fig:not(.is-leaving)').length);
    const groups = state.cast.filter((e) => byId[e.id].kind === 'members').length;
    const pad = innerWidth < 900 ? 48 : 24;
    // tallest figure that still leaves room for its name plate
    const tall = Math.max(60, (line.clientHeight - pad - 56) / 2.5);
    let w, rows = 1;
    if (state.view === 'lodge') {
      // one row, or two rows when that lets the figures stand larger
      const W = line.clientWidth - 32, H = line.clientHeight - pad;
      const one = Math.min(tall, W / (n * 0.8 + groups * 0.55));
      const two = Math.min((H - 2 * 56 - 14) / 5, W / (Math.ceil(n / 2) * 0.8 + groups * 0.55));
      rows = two > one * 1.15 ? 2 : 1;
      w = Math.max(30, rows === 2 ? two : one);
    } else {
      w = innerWidth < 900 ? Math.min(tall, innerWidth * 0.5) : Math.min(tall, Math.max(110, line.clientWidth / 3.3));
    }
    root.style.setProperty('--fig-w', `${Math.floor(w)}px`);
    root.classList.toggle('fr-lodge', state.view === 'lodge');
    root.classList.toggle('fr-two', state.view === 'lodge' && rows === 2);
    if (state.view === 'lodge') {
      // shrink until the whole lodge fits on the stage
      const over = () => Math.max(line.scrollWidth / line.clientWidth, line.scrollHeight / line.clientHeight);
      for (let i = 0; i < 8 && over() > 1.003 && w > 30; i++) {
        w = Math.max(30, w / over() - 1);
        root.style.setProperty('--fig-w', `${Math.floor(w)}px`);
      }
      line.scrollLeft = 0;
    }
  };

  /* ---------- Cast chips ---------- */
  const renderCast = () => {
    const html = offices.map((o) => `<button type="button" class="fr-cast-chip${o.kind === 'members' ? ' is-members' : ''}" data-fr-toggle="${o.id}" aria-pressed="${!!entry(o.id)}">${esc(o.short || o.title)}</button>`).join('');
    root.querySelectorAll('[data-fr-cast]').forEach((box) => { box.innerHTML = html; });
    const ids = state.cast.map((e) => e.id).join();
    root.querySelectorAll('[data-fr-preset]').forEach((b) => b.classList.toggle('is-on', presets[b.dataset.frPreset]().map((o) => o.id).join() === ids));
  };

  /* ---------- The dressing panel ---------- */
  const panel = $('[data-fr-panel]');
  const subtotal = (e) => {
    const o = byId[e.id];
    return KINDS.reduce((sum, kind) => {
      const s = e[kind], p = s && find(o, kind, s.p);
      return p ? sum + variantOf(p, s.v).price : sum;
    }, 0);
  };
  const LABEL = { apron: 'Apron', collar: 'Collar', jewel: 'Jewel' };
  const tabsFor = (o) => KINDS.filter((k) => k === 'apron' || (o.kind !== 'members' && (o[LIST[k]] || []).length));
  const tabPrice = (o, e, kind) => {
    const s = e[kind], p = s && find(o, kind, s.p);
    if (p) return money(variantOf(p, s.v).price);
    if (kind === 'jewel' && includesJewel(e.collar && find(o, 'collar', e.collar.p))) return 'With collar';
    return 'None';
  };
  const pane = (o, e, kind) => {
    const list = o[LIST[kind]] || [];
    const s = e[kind], chosen = s && find(o, kind, s.p), v = chosen && variantOf(chosen, s.v);
    const optional = kind !== 'apron';
    return `
      <div class="frp-rail">
        ${optional ? `<button type="button" class="frp-opt frp-none" data-k="${kind}" data-h="" aria-pressed="${!chosen}" aria-label="No ${LABEL[kind].toLowerCase()}"><span class="frp-none-mark" aria-hidden="true"></span><span>None</span></button>` : ''}
        ${list.map((p) => `<button type="button" class="frp-opt" data-k="${kind}" data-h="${esc(p.handle)}" aria-pressed="${!!chosen && chosen.handle === p.handle}" title="${esc(p.title)}"><img src="${esc(p.thumb)}" alt="" loading="lazy" width="120" height="120"><span>${esc(p.detail || p.name)}</span></button>`).join('')}
      </div>
      ${chosen ? `<div class="frp-chosen">
        ${chosen.variants.length > 1 ? `<label class="frp-var"><span>${esc(chosen.option || 'Option')}</span><select data-k="${kind}" aria-label="${esc(chosen.option || 'Option')}">${chosen.variants.map((x) => `<option value="${x.id}"${x.id === v.id ? ' selected' : ''}${x.available ? '' : ' disabled'}>${esc(x.title)} · ${money(x.price)}${x.available ? '' : ' (sold out)'}</option>`).join('')}</select></label>` : ''}
        <a class="frp-link" href="${esc(chosen.url)}" target="_blank" rel="noopener">${esc(chosen.title)} ↗</a>
      </div>` : ''}
      ${kind === 'jewel' && !o.collars.length ? '<p class="frp-note">No matching collar in this range yet, so the jewel is shown on a simple chain.</p>' : ''}
      ${kind === 'jewel' && includesJewel(e.collar && find(o, 'collar', e.collar.p)) && !chosen ? '<p class="frp-note">The chosen collar already carries the jewel of office.</p>' : ''}`;
  };

  const renderPanel = (animate = true) => {
    const e = current();
    if (!e) {
      panel.innerHTML = `<div class="frp frp-empty"><p class="m-eyebrow">Your line-up is empty</p><h2 class="frp-title">Who is wearing <em>regalia?</em></h2><p class="frp-role">Choose a starting point, or add an office to the line-up.</p></div>`;
      return;
    }
    const o = byId[e.id], i = state.cast.indexOf(e), n = state.cast.length;
    const members = o.kind === 'members', tabs = tabsFor(o);
    if (!tabs.includes(state.tab)) state.tab = 'apron';
    panel.innerHTML = `
      <div class="frp${animate && !calm ? ' is-in' : ''}">
        <div class="frp-head">
          <p class="m-eyebrow">${members ? 'The brethren' : esc(o.where)}<span class="frp-pos">${i + 1} of ${n}</span></p>
          <h2 class="frp-title">${esc(o.title)}</h2>
          ${o.jewelText && !members ? `<p class="frp-jewel">${esc(o.jewelText)}</p>` : ''}
          <p class="frp-role">${esc(o.role)}</p>
          ${members ? `<div class="frp-qty"><span>Brethren</span><div class="frp-step"><button type="button" data-fr-qty="-1" aria-label="Fewer brethren">−</button><input type="number" min="1" max="500" value="${e.qty}" data-fr-qtyin aria-label="Number of brethren"><button type="button" data-fr-qty="1" aria-label="More brethren">+</button></div></div>` : ''}
        </div>
        <div class="frp-tabs" role="tablist" aria-label="Regalia">
          ${tabs.map((k) => `<button type="button" class="frp-tab" role="tab" data-fr-tab="${k}" aria-selected="${k === state.tab}"><b>${LABEL[k]}</b><small>${tabPrice(o, e, k)}</small></button>`).join('')}
        </div>
        <div class="frp-pane" role="tabpanel">${pane(o, e, state.tab)}</div>
        <div class="frp-foot">
          <p class="frp-sub"><span>${members ? 'Each brother' : 'This officer'}</span><strong>${money(subtotal(e))}</strong></p>
          <button type="button" data-fr-step="-1"${i === 0 ? ' disabled' : ''} aria-label="Previous officer">←</button>
          <button type="button" data-fr-step="1"${i === n - 1 ? ' disabled' : ''}>Next <span aria-hidden="true">→</span></button>
        </div>
      </div>`;
    if (animate && !calm) requestAnimationFrame(() => requestAnimationFrame(() => panel.querySelector('.frp')?.classList.remove('is-in')));
  };

  /* ---------- Totals and the order bar ---------- */
  const lineItems = () => {
    const items = [];
    state.cast.forEach((e) => {
      const o = byId[e.id];
      KINDS.forEach((kind) => {
        const s = e[kind], p = s && find(o, kind, s.p);
        if (p) items.push({ office: o.title, kind, p, v: variantOf(p, s.v), qty: e.qty });
      });
    });
    const people = state.cast.reduce((n, e) => n + e.qty, 0);
    if (state.gloves && DATA.gloves && people) {
      items.push({ office: 'Whole lodge', kind: 'gloves', p: DATA.gloves, v: variantOf(DATA.gloves), qty: Math.ceil(people / 12) });
    }
    return items;
  };
  const totals = () => {
    const items = lineItems();
    return {
      items,
      total: items.reduce((n, x) => n + x.v.price * x.qty, 0),
      pieces: items.filter((x) => x.kind !== 'gloves').reduce((n, x) => n + x.qty, 0),
      officers: state.cast.filter((e) => byId[e.id].kind === 'office').length,
      brethren: state.cast.filter((e) => byId[e.id].kind === 'members').reduce((n, e) => n + e.qty, 0)
    };
  };
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const renderBar = () => {
    const t = totals();
    const who = [t.officers ? plural(t.officers, 'officer', 'officers') : '', t.brethren ? plural(t.brethren, 'brother', 'brethren') : ''].filter(Boolean).join(' · ') || 'No one yet';
    $('[data-fr-count]').textContent = t.officers ? plural(t.officers, 'officer', 'officers') : t.brethren ? plural(t.brethren, 'brother', 'brethren') : 'No one yet';
    $('[data-fr-pieces]').textContent = [t.officers && t.brethren ? plural(t.brethren, 'brother', 'brethren') : '', plural(t.pieces, 'piece', 'pieces')].filter(Boolean).join(' · ');
    const portrait = $('[data-fr-portrait]');
    if (portrait) portrait.innerHTML = `<p class="m-eyebrow">Your lodge</p><p class="fr-portrait-title">${esc(who)}</p><p class="fr-portrait-sub">${plural(t.pieces, 'piece', 'pieces')} · ${t.total ? money(t.total) : '—'}</p>`;
    $('[data-fr-total]').textContent = t.total ? money(t.total) : '—';
    $('[data-fr-open]').disabled = !t.items.length;
    root.querySelectorAll('[data-fr-gloves]').forEach((box) => { box.checked = state.gloves; });
    const cc = $('[data-fr-castcount]');
    if (cc) cc.textContent = state.cast.length;
    const i = state.cast.indexOf(current());
    root.querySelectorAll('.fr-nav').forEach((b) => { b.disabled = +b.dataset.frStep < 0 ? i <= 0 : i >= state.cast.length - 1; });
    root.classList.toggle('fr-gloved', state.gloves);
  };

  /* ---------- Review, cart, quote and sharing ---------- */
  const review = $('[data-fr-review]');
  const sheet = $('[data-fr-sheet]');
  const renderReview = () => {
    const t = totals();
    const groups = new Map();
    t.items.forEach((x) => { if (!groups.has(x.office)) groups.set(x.office, []); groups.get(x.office).push(x); });
    $('[data-fr-list]').innerHTML = [...groups].map(([office, items]) => `
      <article class="frr">
        <h3>${esc(office)}${items[0].qty > 1 && items[0].kind !== 'gloves' ? ` <small>× ${items[0].qty}</small>` : ''}</h3>
        <ul>${items.map((x) => `<li><img src="${esc(x.p.thumb)}" alt="" loading="lazy" width="56" height="56"><span><b>${esc(x.p.name)}</b>${x.p.detail ? ` – ${esc(x.p.detail)}` : ''}${x.v.title !== 'Default Title' ? `<small>${esc(x.v.title)}</small>` : ''}${x.kind === 'gloves' ? `<small>${plural(x.qty, 'lot', 'lots')} of 12 pairs</small>` : ''}</span><strong>${money(x.v.price * x.qty)}</strong></li>`).join('')}</ul>
      </article>`).join('');
    $('[data-fr-total2]').textContent = money(t.total);
    $('[data-fr-note]').textContent = t.pieces >= 5
      ? `That is ${plural(t.pieces, 'piece', 'pieces')}. Lodges ordering 5 or more pieces can ask us for lodge pricing before paying.`
      : 'Every order ships free with tracking, worldwide.';
    $('[data-fr-status]').textContent = '';
  };

  const addToCart = async () => {
    const t = totals(), status = $('[data-fr-status]'), btn = $('[data-fr-add]');
    if (!t.items.length) return;
    btn.disabled = true;
    status.textContent = 'Adding your lodge to the cart…';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: t.items.map((x) => ({ id: x.v.id, quantity: x.qty, properties: x.kind === 'gloves' ? {} : { 'Lodge office': x.office } })) })
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.description || body.message || 'Some pieces could not be added.');
      const cart = await (await fetch('/cart.js')).json();
      document.querySelectorAll('.cc_text, [data-rd-count]').forEach((el) => { el.textContent = cart.item_count; el.hidden = !cart.item_count; });
      status.innerHTML = `Your lodge is in the cart. <a href="${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart">View cart</a> or <a href="/checkout">go to checkout</a>.`;
    } catch (err) {
      status.textContent = `${err.message} You can also request a quote and we will put the order together for you.`;
    } finally {
      btn.disabled = false;
    }
  };

  const quote = () => {
    const t = totals();
    const lines = ['Lodge fitting room enquiry', ''];
    let office = '';
    t.items.forEach((x) => {
      if (x.office !== office) {
        office = x.office;
        lines.push('', x.qty > 1 && x.kind !== 'gloves' ? `${office} × ${x.qty}` : office);
      }
      const variant = x.v.title !== 'Default Title' ? ` (${x.v.title})` : '';
      lines.push(x.kind === 'gloves'
        ? `- ${x.p.title}: ${plural(x.qty, 'lot', 'lots')} – ${money(x.v.price * x.qty)}`
        : `- ${x.p.title}${variant} – ${money(x.v.price)}${x.qty > 1 ? ' each' : ''}`);
    });
    lines.push('', `Total at list prices: ${money(t.total)} (${plural(t.pieces, 'piece', 'pieces')})`, '', 'Lodge name and number:', 'Country:', 'Needed by:');
    try { sessionStorage.setItem('riaz-finder-enquiry', JSON.stringify({ text: lines.join('\n'), time: Date.now() })); } catch {}
    location.href = `${DATA.quote}${DATA.quote.includes('?') ? '&' : '?'}from=regalia-finder`;
  };

  const share = async () => {
    const url = `${location.origin}${location.pathname}${location.search}#lodge=${encodeURIComponent(encode())}`;
    const status = $('[data-fr-status]');
    try {
      if (navigator.share && matchMedia('(pointer: coarse)').matches) await navigator.share({ title: document.title, url });
      else { await navigator.clipboard.writeText(url); status.textContent = 'Link copied. Send it to your lodge brothers.'; }
    } catch {
      status.textContent = url;
    }
  };

  /* ---------- First-visit hint ---------- */
  const hint = $('[data-fr-hint]');
  const HINT = 'riaz-fitting-hint';
  let hinted = false;
  try { hinted = localStorage.getItem(HINT) === '1'; } catch {}
  if (hinted) hint?.remove();
  const dismissHint = () => {
    if (hinted) return;
    hinted = true;
    hint?.classList.add('is-gone');
    setTimeout(() => hint?.remove(), 600);
    try { localStorage.setItem(HINT, '1'); } catch {}
  };

  /* ---------- Changes ---------- */
  const update = (opts = {}) => {
    if (opts.line) renderLine(); else markSelected(opts.scroll !== false);
    if (opts.cast) renderCast();
    renderPanel(opts.panelAnim !== false);
    renderBar();
    save();
  };

  const setCast = (list) => {
    state.cast = list.map((o) => entry(o.id) || dress(o, state.style));
    if (!entry(state.sel)) state.sel = state.cast[0]?.id || null;
    update({ line: true, cast: true });
  };

  const toggle = (id) => {
    const o = byId[id];
    if (entry(id)) {
      state.cast = state.cast.filter((e) => e.id !== id);
      if (state.sel === id) state.sel = state.cast[0]?.id || null;
    } else {
      const next = dress(o, state.style);
      const order = offices.map((x) => x.id);
      state.cast.push(next);
      state.cast.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
      state.sel = id;
    }
    update({ line: true, cast: true });
  };

  const select = (id) => {
    dismissHint();
    if (!entry(id) || state.sel === id) return;
    state.sel = id;
    update();
  };

  const step = (dir) => {
    const i = state.cast.indexOf(current()), next = state.cast[i + dir];
    if (next) { state.sel = next.id; update(); figs.get(next.id)?.focus({ preventScroll: true }); }
  };

  const choose = (kind, handle) => {
    dismissHint();
    const e = current(), o = byId[e.id];
    const p = handle ? find(o, kind, handle) : null;
    if (kind !== 'apron' || p) e[kind] = p ? { p: p.handle, v: variantOf(p, e[kind]?.p === p.handle ? e[kind].v : null).id } : null;
    const changed = [kind];
    if (kind === 'collar' && p && includesJewel(p) && e.jewel) { e.jewel = null; changed.push('jewel'); }
    if (kind === 'jewel' && p && includesJewel(e.collar && find(o, 'collar', e.collar.p))) { e.collar = choice(o.collars.find((x) => !includesJewel(x))); changed.push('collar'); }
    dressFig(figs.get(e.id), e, changed);
    update({ scroll: false, panelAnim: false });
  };

  const setStyle = (style) => {
    state.style = style;
    root.querySelectorAll('[data-fr-style]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.frStyle === style)));
    state.cast.forEach((e) => {
      const o = byId[e.id], p = pick(o.aprons, style);
      if (p && p.handle !== e.apron?.p) {
        e.apron = choice(p);
        const el = figs.get(e.id);
        if (el) dressFig(el, e, ['apron']);
      }
    });
    update({ scroll: false, panelAnim: false });
  };

  const setView = (view) => {
    state.view = view;
    root.querySelectorAll('[data-fr-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.frView === view)));
    sizeView();
    markSelected();
    if (!calm) line.animate([{ opacity: 0.15, transform: 'scale(.985)' }, { opacity: 1, transform: 'none' }], { duration: 650, easing: 'cubic-bezier(.16,1,.3,1)' });
  };

  root.addEventListener('click', (ev) => {
    const t = ev.target;
    const tab = t.closest('[data-fr-tab]');
    if (tab) { state.tab = tab.dataset.frTab; renderPanel(false); return; }
    if (t.closest('[data-fr-sheet-open]')) { sheet.showModal(); return; }
    if (t.closest('[data-fr-sheet-close]') || t === sheet) { sheet.close(); return; }
    const preset = t.closest('[data-fr-preset]');
    if (preset) { setCast(presets[preset.dataset.frPreset]()); return; }
    const chip = t.closest('[data-fr-toggle]');
    if (chip) { toggle(chip.dataset.frToggle); return; }
    const fig = t.closest('.fr-fig');
    if (fig) { select(fig.dataset.id); return; }
    const opt = t.closest('.frp-opt');
    if (opt) { choose(opt.dataset.k, opt.dataset.h); return; }
    const st = t.closest('[data-fr-step]');
    if (st) { step(+st.dataset.frStep); return; }
    const sty = t.closest('[data-fr-style]');
    if (sty) { setStyle(sty.dataset.frStyle); return; }
    const vw = t.closest('[data-fr-view]');
    if (vw) { setView(vw.dataset.frView); return; }
    const q = t.closest('[data-fr-qty]');
    if (q) { const e = current(); e.qty = Math.max(1, Math.min(500, e.qty + +q.dataset.frQty)); dressFig(figs.get(e.id), e, []); update({ scroll: false, panelAnim: false }); return; }
    if (t.closest('[data-fr-open]')) { renderReview(); review.showModal(); return; }
    if (t.closest('[data-fr-close]') || t === review) { review.close(); return; }
    if (t.closest('[data-fr-add]')) { addToCart(); return; }
    if (t.closest('[data-fr-quote]')) { quote(); return; }
    if (t.closest('[data-fr-share]')) share();
  });

  root.addEventListener('change', (ev) => {
    const t = ev.target;
    if (t.matches('[data-fr-gloves]')) { state.gloves = t.checked; update({ scroll: false, panelAnim: false }); return; }
    if (t.matches('select[data-k]')) {
      const e = current();
      e[t.dataset.k].v = +t.value;
      update({ scroll: false, panelAnim: false });
      return;
    }
    if (t.matches('[data-fr-qtyin]')) {
      const e = current();
      e.qty = Math.max(1, Math.min(500, Math.round(+t.value) || 1));
      dressFig(figs.get(e.id), e, []);
      update({ scroll: false, panelAnim: false });
    }
  });

  line.addEventListener('keydown', (ev) => {
    if (ev.key === 'ArrowRight' || ev.key === 'ArrowLeft') { ev.preventDefault(); step(ev.key === 'ArrowRight' ? 1 : -1); }
  });

  let resizeTimer;
  const resized = () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(() => { sizeView(); markSelected(false); }, 90); };
  if ('ResizeObserver' in window) new ResizeObserver(resized).observe(line);
  else addEventListener('resize', resized, { passive: true });

  /* ---------- Start ---------- */
  const saved = restore();
  if (saved) {
    state = { ...state, ...saved };
    state.sel = state.cast[0]?.id || null;
    root.querySelectorAll('[data-fr-style]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.frStyle === state.style)));
    update({ line: true, cast: true });
  } else {
    setCast(presets.principal());
  }
  root.classList.add('fr-ready');
  if (location.hash.startsWith('#lodge=')) history.replaceState(null, '', location.pathname + location.search);
})();
